/* Số tiền, định dạng và đọc số thành chữ (VI/EN). Port nguyên văn từ app cũ. */

export type Currency = 'VND' | 'USD';

const n0 = (v: unknown): number => Number(v) || 0;

export const round = (n: unknown, cur: Currency | string): number =>
  cur === 'USD' ? Math.round(n0(n) * 100) / 100 : Math.round(n0(n));

export const fmt = (n: unknown, cur: Currency | string): string =>
  cur === 'USD'
    ? n0(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : Math.round(n0(n)).toLocaleString('vi-VN');

export const fmtEn = (n: unknown, cur: Currency | string): string =>
  n0(n).toLocaleString('en-US', cur === 'USD' ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : { maximumFractionDigits: 0 });

const DIG = ['không', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];

function viTriple(n: number, full: boolean): string {
  const h = Math.floor(n / 100), t = Math.floor(n / 10) % 10, u = n % 10, w: string[] = [];
  if (full || h) w.push(DIG[h] + ' trăm');
  if (t === 0 && u === 0) return w.join(' ');
  if (t === 0) { if (full || h) w.push('linh'); w.push(DIG[u]); }
  else if (t === 1) { w.push('mười'); if (u === 5) w.push('lăm'); else if (u) w.push(DIG[u]); }
  else { w.push(DIG[t] + ' mươi'); if (u === 1) w.push('mốt'); else if (u === 5) w.push('lăm'); else if (u) w.push(DIG[u]); }
  return w.join(' ');
}

export function viInt(n: number): string {
  n = Math.floor(Math.abs(n));
  if (n === 0) return 'không';
  const units = ['', 'nghìn', 'triệu', 'tỷ', 'nghìn tỷ', 'triệu tỷ'];
  const g: number[] = [];
  while (n > 0) { g.push(n % 1000); n = Math.floor(n / 1000); }
  const out: string[] = [];
  for (let i = g.length - 1; i >= 0; i--) {
    if (g[i] === 0) continue;
    out.push(viTriple(g[i], i < g.length - 1) + (units[i] ? ' ' + units[i] : ''));
  }
  return out.join(' ');
}

const ONES = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function enTriple(n: number): string {
  const h = Math.floor(n / 100), r = n % 100, w: string[] = [];
  if (h) w.push(ONES[h] + ' hundred');
  if (r) { if (h) w.push('and'); w.push(r < 20 ? ONES[r] : TENS[Math.floor(r / 10)] + (r % 10 ? '-' + ONES[r % 10] : '')); }
  return w.join(' ');
}

export function enInt(n: number): string {
  n = Math.floor(Math.abs(n));
  if (n === 0) return 'zero';
  const sc = ['', 'thousand', 'million', 'billion', 'trillion'];
  const g: number[] = [];
  while (n > 0) { g.push(n % 1000); n = Math.floor(n / 1000); }
  const out: string[] = [];
  for (let i = g.length - 1; i >= 0; i--) if (g[i]) out.push(enTriple(g[i]) + (sc[i] ? ' ' + sc[i] : ''));
  return out.join(' ');
}

export const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export interface AmountWords { vi: string; en: string }

export function words(amount: number, cur: Currency | string): AmountWords {
  const neg = amount < 0; amount = Math.abs(amount);
  if (cur === 'USD') {
    const d = Math.floor(amount + 1e-9), c = Math.round((amount - d) * 100);
    const vi = viInt(d) + ' đô la Mỹ' + (c ? ' và ' + viInt(c) + ' xu' : ' chẵn');
    const en = enInt(d) + ' US dollar' + (d === 1 ? '' : 's') + (c ? ' and ' + String(c).padStart(2, '0') + '/100' : ' only');
    return { vi: (neg ? 'Âm ' : '') + cap(vi) + '.', en: (neg ? 'Minus ' : '') + cap(en) + '.' };
  }
  return { vi: (neg ? 'Âm ' : '') + cap(viInt(amount) + ' đồng chẵn') + '.', en: (neg ? 'Minus ' : '') + cap(enInt(amount) + ' Vietnam dong only') + '.' };
}

/** Số tiền bằng chữ tiếng Anh dùng trên Invoice (mẫu Flyone). */
export function invWords(amount: number, cur: Currency | string): string {
  amount = Math.abs(amount);
  if (cur === 'USD') {
    const d = Math.floor(amount + 1e-9), c = Math.round((amount - d) * 100);
    return cap(enInt(d)) + ' dollar' + (d === 1 ? '' : 's') + (c ? ' and ' + enInt(c) + ' cent' + (c === 1 ? '' : 's') : '') + '.';
  }
  return cap(enInt(Math.round(amount))) + ' Vietnam dong.';
}
